import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, styled } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { ControlePatrimonio } from "../../../../domain/models/controle_patrimonio";
import { EquipamentoDropDown } from "../../../../domain/models/equipamento_dropdown";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { zodResolver } from "@hookform/resolvers/zod";
import { ControlePatrimonioForm, ControlePatrimonioSchema } from "../../../schemas/Funcionarios_cadastro/controle_patrimonio_schema";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined, normalizeFileName } from "../../../../../../common/utils/utils";

import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * CadastroControlePatrimonioModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  loading: boolean,
  selectedControlePatrimonio: ControlePatrimonio | null,
  equipamentoLista: EquipamentoDropDown[],
  onSubmit: (form: ControlePatrimonioForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onDownload: (idControlePatrimonio: number, imageName: string) => void,
  onClose: (idControlePatrimonio: number | null, openState: boolean) => void,
};

export const CadastroControlePatrimonioModal = ({
  open,
  saving,
  loading,
  selectedControlePatrimonio,
  equipamentoLista,
  onSubmit,
  onOpenChangeHistory,
  onDownload,
  onClose,
}: Props) => {
  const [controlePatrimonio, setControlePatrimonio] = useState(selectedControlePatrimonio);

  const hookForm = useForm<ControlePatrimonioForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ControlePatrimonioSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedControlePatrimonio) {
        hookForm.reset();

        if (selectedControlePatrimonio.idMaquinaEquip) {
          hookForm.setValue('idMaquinaEquip', selectedControlePatrimonio.idMaquinaEquip);
        }

        hookForm.setValue('dataEntrega', selectedControlePatrimonio.dataEntrega);
        hookForm.setValue('dataDevolucao', selectedControlePatrimonio.dataDevolucao);
        hookForm.setValue('observacoes', selectedControlePatrimonio.observacao);
        hookForm.setValue('limparArquivos', false);
      } else {
        hookForm.reset();
      }
      setControlePatrimonio(selectedControlePatrimonio ?? null);
    }
  }, [
    hookForm,
    open,
    selectedControlePatrimonio,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose(null, false);
  }, [
    saving,
    onClose,
  ]);

  const handleDownload = useCallback(() => {
    if (!saving && controlePatrimonio) onDownload(controlePatrimonio.idControlePatrimonio, controlePatrimonio.nomeArquivo ?? '')
  }, [
    saving,
    controlePatrimonio,
    onDownload,
  ]);

  const handleDeleteFile = useCallback(() => {
    if (!saving) {
      if (window.confirm('Você deseja realmente excluir este item?')) {
        hookForm.setValue('limparArquivos', true);
        setControlePatrimonio(null);
      }
    }
  }, [
    saving,
    hookForm,
  ]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving) {
      hookForm.setValue('limparArquivos', false);
      const file = e.target.files?.[0];

      if (file) {
        onChange(file);
      } else {
        onChange(null);
      }
    }
  }, [hookForm, saving]);

  const handleChangeHistory = useCallback(() => {
    if (selectedControlePatrimonio) {
      onOpenChangeHistory(selectedControlePatrimonio.idControlePatrimonio, LogTipoTabela.CONTROLE_PATRIMONIO);
    }
  }, [selectedControlePatrimonio, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(onSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>
      <DialogTitle>
        {selectedControlePatrimonio ? "Editar" : "Cadastrar"} Controle de Patrimônio
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving || loading}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <DefaultFormAutocomplete
                name='idMaquinaEquip'
                label='Máquina/Equipamento'
                readOnly={saving || loading}
                required={true}
                options={equipamentoLista}
                getOptionLabel={(opt: EquipamentoDropDown) => opt.descricao}
                getOptionKey={(opt: EquipamentoDropDown) => opt.idEquipamento}
                reactHookForm={hookForm} />

              <FormDatePickerInput
                name='dataEntrega'
                label='Data Entrega'
                readOnly={saving || loading}
                reactHookForm={hookForm} />

              <FormDatePickerInput
                name='dataDevolucao'
                label='Data Devolução'
                readOnly={saving || loading}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name={'observacoes'}
                label={"Observações"}
                readOnly={saving || loading}
                multiline={true}
                rows={7}
                reactHookForm={hookForm}
              />

              <div className="flex w-full">
                <Controller
                  name='files'
                  control={hookForm.control}
                  render={({ field }) => (controlePatrimonio && controlePatrimonio.arquivo && controlePatrimonio.arquivo.length > 0) ? (
                    <div className="w-full flex flex-row justify-between items-center">
                      <span className="cursor-pointer" onMouseDown={handleDownload}>
                        {controlePatrimonio.nomeArquivo && controlePatrimonio.nomeArquivo.length > 40
                          ? `${controlePatrimonio.nomeArquivo.substring(0, 40)}...`
                          : controlePatrimonio.nomeArquivo}
                      </span>

                      <IconButton onClick={handleDeleteFile}>
                        <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                      </IconButton>
                    </div>
                  ) : (
                    <PrimaryButton
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      disabled={saving || loading}
                      startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                      {field.value ? normalizeFileName(field.value?.name, 40) : "Escolher arquivo"}
                      <VisuallyHiddenInput
                        type="file"
                        onChange={(e) => handleFileChange(e, field.onChange)}
                        multiple={false} />
                    </PrimaryButton>
                  )} />
              </div>
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedControlePatrimonio) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  onClick={hookForm.handleSubmit(onSubmit, (erros) => console.log("Erros de validação:", erros))}
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}
